import type { RefObject } from "react";
import type { RunIssue } from "../../../lib/api";

function MicGlyph({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

const VISIBLE = 6;

export type ProjectCardTab = {
  projectId: string;
  name: string;
  description?: string;
  customer?: string;
  owner?: string;
  status?: string;
  rollups: { yoursOpen: number; blocked: number; doneSince: number };
  delta: string;
  issues: RunIssue[];
};

export function ProjectCard({
  tab,
  index,
  total,
  tickedKeys,
  typedLine,
  onToggleIssue,
  onTypedChange,
  showAll,
  onShowAll,
  focusInputRef,
  speechListening,
  speechSupported,
  onToggleSpeech,
  speechError,
}: {
  tab: ProjectCardTab;
  index: number;
  total: number;
  tickedKeys: Set<string>;
  typedLine: string;
  onToggleIssue: (key: string) => void;
  onTypedChange: (v: string) => void;
  showAll: boolean;
  onShowAll: () => void;
  focusInputRef: RefObject<HTMLInputElement | null>;
  speechListening: boolean;
  speechSupported: boolean;
  onToggleSpeech: () => void;
  speechError?: string | null;
}) {
  const kindStatus = [tab.customer, tab.status?.replace(/_/g, " ")].filter(Boolean).join(" / ");
  const eyebrowBits = [
    `PROJECT ${index} / ${total}`,
    kindStatus || undefined,
    tab.owner ? `owner ${tab.owner}` : undefined,
  ].filter(Boolean);
  const visible = showAll ? tab.issues : tab.issues.slice(0, VISIBLE);
  const preview: string[] = [
    ...tab.issues.filter((i) => tickedKeys.has(i.key)).map((i) => i.summary),
    ...(typedLine.trim() ? [typedLine.trim()] : []),
  ];

  return (
    <div className="rs-card">
      <span className="rs-eyebrow">{eyebrowBits.join(" · ")}</span>
      <div className="rs-proj-name">{tab.name}</div>
      {tab.description && <div className="rs-proj-sub">{tab.description}</div>}

      <div className="rs-sub" style={{ marginBottom: 12 }}>
        {tab.rollups.yoursOpen} yours open · {tab.rollups.blocked} blocked · +{tab.rollups.doneSince} done since yesterday
      </div>

      {tab.delta && (
        <div className="rs-ev" style={{ marginBottom: 16 }}>
          <div className="rs-ev-hd"><span className="rs-eyebrow">AGENT · SINCE YESTERDAY</span></div>
          <div className="rs-ev-row"><span className="rs-chip">SUMMARY</span><span>{tab.delta}</span></div>
        </div>
      )}

      <div className="rs-yrows" style={{ marginTop: 4 }}>
        {tab.issues.length === 0 && (
          <div className="rs-empty" style={{ paddingTop: 8 }}>No open issues assigned to you on this project.</div>
        )}
        {visible.map((issue) => {
          const on = tickedKeys.has(issue.key);
          return (
            <div key={issue.key} className="rs-yrow">
              <button
                type="button"
                className={`rs-ycheck${on ? " on" : ""}`}
                aria-pressed={on}
                onClick={() => onToggleIssue(issue.key)}
              >
                {on ? "✓" : ""}
              </button>
              <div className="rs-ymain">
                <div className={`rs-ytext${on ? " done" : ""}`}>
                  {issue.blocked && <span className="rs-carry" style={{ marginRight: 8 }}>blocked</span>}
                  {issue.summary}
                </div>
                <div className="rs-yproj">
                  <a href={issue.url} target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
                    {issue.key}
                  </a>
                  {" · "}
                  {issue.status}
                  {issue.priority ? ` · ${issue.priority}` : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {!showAll && tab.issues.length > VISIBLE && (
        <button type="button" className="rs-btn ghost" style={{ marginTop: 8 }} onClick={onShowAll}>
          Show all {tab.issues.length}
        </button>
      )}

      <div style={{ marginTop: 18 }}>
        <span className="rs-eyebrow">OR TYPE SOMETHING ELSE</span>
        <div className="rs-focusrow" style={{ marginTop: 6 }}>
          <input
            ref={focusInputRef as RefObject<HTMLInputElement>}
            className="rs-input"
            value={typedLine}
            placeholder="…or type something else on this project"
            onChange={(e) => onTypedChange(e.target.value)}
          />
          <button
            type="button"
            className={`rs-mic${speechListening ? " on" : ""}`}
            onClick={onToggleSpeech}
            disabled={!speechSupported}
            aria-label="Dictate focus"
          >
            <MicGlyph />
          </button>
        </div>
        {speechListening && (
          <div className="rs-recline"><span className="rs-dot" />RECORDING — words land in the free-text line</div>
        )}
        {speechError && <div style={{ fontSize: 11.5, color: "#cf4040", marginTop: 6 }}>{speechError}</div>}
      </div>

      <div style={{ marginTop: 18 }}>
        <span className="rs-eyebrow">TODAY ON {tab.name.toUpperCase()}</span>
        {preview.length === 0
          ? <div className="rs-empty" style={{ paddingTop: 8 }}>Nothing staged — will park this project.</div>
          : (
            <div className="rs-yrows">
              {preview.map((t, i) => (
                <div key={i} className="rs-yrow" style={{ padding: "8px 0" }}>
                  <div className="rs-ytext">{t}</div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
